import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lifecycle states for a debounced-autosave field/form.
 *
 *   idle     — value has never been touched (or has just been reset/saved
 *              and faded back to idle).
 *   unsaved  — local edits exist that have not yet been persisted.
 *   saving   — a save is currently in flight.
 *   saved    — the most recent save succeeded; nothing dirty.
 *   error    — the most recent save threw; local edits are intact and the
 *              caller can retry by calling flush() (or just keep typing).
 */
export type AutosaveStatus =
  | 'idle'
  | 'unsaved'
  | 'saving'
  | 'saved'
  | 'error';

export interface UseDebouncedAutosaveOptions<T> {
  /**
   * The initial value the form starts with. Treated as already-persisted
   * (no save fires for it). Only the *initial* render reads this — to
   * replace the value later, call `reset(newValue)`.
   */
  initialValue: T;

  /**
   * Persist a value to the backend. Throw to signal failure.
   * The hook will not call this concurrently — overlapping save
   * requests are serialized.
   */
  save: (value: T) => Promise<unknown>;

  /**
   * Milliseconds to wait after the last edit before firing an autosave.
   * Default: 1000.
   */
  debounceMs?: number;

  /**
   * If set, after a successful save the status will revert to `idle`
   * after this many ms. Set to 0 or omit to keep the `saved` state
   * sticky until the next edit. Default: 2000.
   */
  savedStatusFadeMs?: number;
}

export interface UseDebouncedAutosaveResult<T> {
  /** Current in-memory value. Source of truth while editing. */
  value: T;

  /**
   * Replace the value. Accepts either a new value or an updater function
   * (like React's setState). Marks the form dirty and schedules a save.
   */
  setValue: (updater: T | ((prev: T) => T)) => void;

  /**
   * Convenience for object-shaped values: shallow-merge a partial patch
   * into the current value. Marks dirty and schedules a save.
   */
  patch: (partial: Partial<T>) => void;

  /** See {@link AutosaveStatus}. */
  status: AutosaveStatus;

  /** The error from the most recent failed save, if any. */
  error: Error | null;

  /**
   * Flush any pending changes immediately. Awaits the in-flight save
   * (if any) and then triggers a fresh save with the latest value if
   * the form is dirty. Resolves when the save completes (or rejects on
   * error). Idempotent: calling it on a clean form is a no-op.
   */
  flush: () => Promise<void>;

  /**
   * Replace the value and clear all pending/saving state. Use this when
   * the underlying source-of-truth changes for an external reason
   * (e.g. user signed out and back in as someone else).
   */
  reset: (newValue: T) => void;

  /** True if there are unsaved local edits. */
  isDirty: boolean;

  /** Wall-clock time of the last successful save in this session. */
  lastSavedAt: Date | null;
}

/**
 * useDebouncedAutosave
 *
 * Owns local form state for autosaving forms. Keeps every keystroke
 * entirely client-side, batches saves on a debounce, and prevents
 * overlapping requests.
 *
 *   const form = useDebouncedAutosave({
 *     initialValue: profile,
 *     save: (next) => updateProfile(next),
 *     debounceMs: 1000,
 *   });
 *
 *   <input
 *     value={form.value.name ?? ''}
 *     onChange={(e) => form.patch({ name: e.target.value })}
 *   />
 *
 *   <button onClick={() => form.flush()}>Save Draft</button>
 *   {form.status === 'saving' && <Saving />}
 *
 * Design notes:
 *  - The hook never refetches. After a successful save, the caller's
 *    save() return value (if used) can be written to a query cache, but
 *    the hook itself does not invalidate or refetch — that's why typing
 *    feels instant.
 *  - Concurrency: at most one save is in flight at a time. If a save is
 *    running when a new one is requested, the new one waits, then re-
 *    checks whether the form is still dirty before firing.
 *  - The hook never re-replaces `value` from the outside on its own.
 *    Inputs bound to `form.value` will not remount, so cursor position
 *    is preserved.
 */
export function useDebouncedAutosave<T>({
  initialValue,
  save,
  debounceMs = 1000,
  savedStatusFadeMs = 2000,
}: UseDebouncedAutosaveOptions<T>): UseDebouncedAutosaveResult<T> {
  const [value, setValueState] = useState<T>(initialValue);
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Refs coordinate timers and the in-flight save so we don't depend on
  // stale closures.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const latestValueRef = useRef<T>(initialValue);
  const dirtyRef = useRef<boolean>(false);
  const saveRef = useRef(save);

  // Keep saveRef pointing at the latest save fn so performSave's closure
  // doesn't go stale across renders without us re-running performSave.
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  /**
   * Perform a save now. Serialized — if another save is in flight, wait
   * for it and re-check dirty state.
   */
  const performSave = useCallback(async (): Promise<void> => {
    // Serialize: wait for any in-flight save first.
    if (inFlightRef.current) {
      try {
        await inFlightRef.current;
      } catch {
        // Previous save's error is already surfaced on `status`; we
        // don't need to do anything with it here.
      }
    }
    if (!dirtyRef.current) return;

    const valueAtSaveStart = latestValueRef.current;
    setStatus('saving');
    setError(null);

    const promise = (async () => {
      try {
        await saveRef.current(valueAtSaveStart);
        // If no further edits happened during the save, we're clean.
        if (latestValueRef.current === valueAtSaveStart) {
          dirtyRef.current = false;
          setStatus('saved');
          setLastSavedAt(new Date());
          // Optional fade from 'saved' back to 'idle' so the indicator
          // doesn't permanently shout "Saved" at the user.
          if (savedStatusFadeMs > 0) {
            clearFadeTimer();
            fadeTimerRef.current = setTimeout(() => {
              fadeTimerRef.current = null;
              // Only fade if we're still in the saved state — a fresh
              // edit may have moved us to 'unsaved' in the meantime.
              setStatus((s) => (s === 'saved' ? 'idle' : s));
            }, savedStatusFadeMs);
          }
        } else {
          // The user kept typing during the save; we're still dirty.
          setStatus('unsaved');
        }
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setStatus('error');
      }
    })();

    inFlightRef.current = promise;
    try {
      await promise;
    } finally {
      // Only clear if the same promise is still the in-flight ref. A
      // serialized follow-up save may have already taken the slot.
      if (inFlightRef.current === promise) {
        inFlightRef.current = null;
      }
    }
  }, [clearFadeTimer, savedStatusFadeMs]);

  const setValue = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: T) => T)(prev)
            : updater;
        latestValueRef.current = next;
        return next;
      });
      dirtyRef.current = true;
      setStatus('unsaved');
      clearFadeTimer();

      clearDebounceTimer();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        void performSave();
      }, debounceMs);
    },
    [clearDebounceTimer, clearFadeTimer, debounceMs, performSave],
  );

  const patch = useCallback(
    (partial: Partial<T>) => {
      setValue((prev) => ({ ...(prev as object), ...partial }) as T);
    },
    [setValue],
  );

  const flush = useCallback(async (): Promise<void> => {
    clearDebounceTimer();
    if (!dirtyRef.current && !inFlightRef.current) return;
    await performSave();
  }, [clearDebounceTimer, performSave]);

  const reset = useCallback(
    (newValue: T) => {
      clearDebounceTimer();
      clearFadeTimer();
      latestValueRef.current = newValue;
      dirtyRef.current = false;
      setValueState(newValue);
      setStatus('idle');
      setError(null);
    },
    [clearDebounceTimer, clearFadeTimer],
  );

  // Cleanup on unmount: cancel any pending debounce / fade timer. We do
  // NOT await an in-flight save on unmount — the parent should call
  // flush() explicitly if it wants persistence guarantees on navigation.
  useEffect(() => {
    return () => {
      clearDebounceTimer();
      clearFadeTimer();
    };
  }, [clearDebounceTimer, clearFadeTimer]);

  return {
    value,
    setValue,
    patch,
    status,
    error,
    flush,
    reset,
    isDirty:
      status === 'unsaved' || status === 'saving' || status === 'error',
    lastSavedAt,
  };
}
